import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Alert,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip
} from '@mui/material';
import {
  Close,
  ErrorOutline,
  Send
} from '@mui/icons-material';
import { orderDisplayNumber } from '../utils/orderDisplay';

const UnavailableItemsModal = ({ order, open, onSubmit, onCancel }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Сбрасываем состояния при смене заказа или открытии/закрытии модального окна
  useEffect(() => {
    if (open && order) {
      console.log(`[UnavailableItemsModal] Открыто для заказа #${order.id}, сброс состояний`);
      setIsProcessing(false);
      setSelectedItems([]);
      setError('');
    }
  }, [order?.id, open]);

  // Формируем уникальный ключ для позиции
  const getItemKey = (item, index) => {
    const itemName = item.item_name || item.name || 'Позиция';
    const sizeName = item.size_name || '';
    return `item-${index}-${itemName}-${sizeName}`;
  };

  // Формируем уникальный ключ для добавки
  const getAddonKey = (itemIndex, addon, addonIndex) => {
    const addonName = addon.addon_name || addon.name || 'Добавка';
    return `addon-${itemIndex}-${addonIndex}-${addonName}`;
  };

  // Формируем текст для позиции
  const getItemDisplayName = (item) => {
    const itemName = item.item_name || item.name || 'Позиция';
    const sizeName = item.size_name || '';
    return sizeName ? `${itemName} (${sizeName})` : itemName;
  };

  // Формируем текст для добавки
  const getAddonDisplayName = (addon) => {
    return addon.addon_name || addon.name || 'Добавка';
  };

  // Обработчик выбора позиции
  const handleToggleItem = (itemKey) => {
    setSelectedItems(prev => {
      if (prev.includes(itemKey)) {
        return prev.filter(key => key !== itemKey);
      } else {
        return [...prev, itemKey];
      }
    });
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      setError('Пожалуйста, выберите хотя бы одну позицию');
      return;
    }

    setIsProcessing(true);
    setError('');
    
    try {
      // Формируем текстовое описание из выбранных позиций и добавок
      const items = order.items || [];
      const unavailableList = [];

      items.forEach((item, itemIndex) => {
        const itemKey = getItemKey(item, itemIndex);
        if (selectedItems.includes(itemKey)) {
          unavailableList.push(getItemDisplayName(item));
        }
        
        // Проверяем добавки
        if (item.addons && item.addons.length > 0) {
          item.addons.forEach((addon, addonIndex) => {
            const addonKey = getAddonKey(itemIndex, addon, addonIndex);
            if (selectedItems.includes(addonKey)) {
              unavailableList.push(`${getAddonDisplayName(addon)} (к ${getItemDisplayName(item)})`);
            }
          });
        }
      });

      const unavailableDescription = unavailableList.join(', ');

      await onSubmit(`Недоступны: ${unavailableDescription}`);
      setSelectedItems([]);
    } catch (err) {
      console.error('Ошибка отправки информации:', err);
      setError('Произошла ошибка. Попробуйте снова.');
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      setSelectedItems([]);
      setError('');
      onCancel();
    }
  };

  if (!order) return null;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <Box
        sx={{
          bgcolor: 'error.light',
          color: 'error.contrastText',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Box display="flex" alignItems="center">
          <ErrorOutline sx={{ fontSize: 32, mr: 1 }} />
          <Typography variant="h6" component="h2">
            Укажите недостающие позиции
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          disabled={isProcessing}
          sx={{ color: 'inherit' }}
        >
          <Close />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Заказ #{orderDisplayNumber(order)} будет возвращен в статус ожидания подтверждения с комментарием о недостающих позициях.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Выберите позиции, которых нет в наличии:
        </Typography>

        <List
          sx={{
            mt: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            maxHeight: 300,
            overflow: 'auto'
          }}
        >
          {order.items && order.items.length > 0 ? (
            order.items.map((item, index) => {
              const itemKey = getItemKey(item, index);
              const isSelected = selectedItems.includes(itemKey);
              const hasAddons = item.addons && item.addons.length > 0;

              return (
                <Box key={itemKey}>
                  {/* Основная позиция */}
                  <ListItem
                    disablePadding
                    divider={!hasAddons && index < order.items.length - 1}
                  >
                    <ListItemButton
                      onClick={() => handleToggleItem(itemKey)}
                      disabled={isProcessing}
                      dense
                      sx={{
                        py: 1.5,
                        bgcolor: isSelected ? 'error.lighter' : 'transparent',
                        '&:hover': {
                          bgcolor: isSelected ? 'error.light' : 'action.hover'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 42 }}>
                        <Checkbox
                          edge="start"
                          checked={isSelected}
                          tabIndex={-1}
                          disableRipple
                          color="error"
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip 
                              label={`${item.quantity || 1}x`}
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 'bold', minWidth: 40 }}
                            />
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                textDecoration: isSelected ? 'line-through' : 'none',
                                color: isSelected ? 'error.main' : 'text.primary'
                              }}
                            >
                              {getItemDisplayName(item)}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>

                  {/* Добавки с отдельными чекбоксами */}
                  {hasAddons && item.addons.map((addon, addonIndex) => {
                    const addonKey = getAddonKey(index, addon, addonIndex);
                    const isAddonSelected = selectedItems.includes(addonKey);
                    const isLastAddon = addonIndex === item.addons.length - 1;
                    const isLastItem = index === order.items.length - 1;

                    return (
                      <ListItem
                        key={addonKey}
                        disablePadding
                        divider={isLastAddon && !isLastItem}
                        sx={{ pl: 4 }}
                      >
                        <ListItemButton
                          onClick={() => handleToggleItem(addonKey)}
                          disabled={isProcessing}
                          dense
                          sx={{
                            py: 1,
                            bgcolor: isAddonSelected ? 'error.lighter' : 'grey.50',
                            '&:hover': {
                              bgcolor: isAddonSelected ? 'error.light' : 'grey.100'
                            }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              checked={isAddonSelected}
                              tabIndex={-1}
                              disableRipple
                              color="error"
                              size="small"
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  textDecoration: isAddonSelected ? 'line-through' : 'none',
                                  color: isAddonSelected ? 'error.main' : 'text.secondary'
                                }}
                              >
                                + {getAddonDisplayName(addon)}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </Box>
              );
            })
          ) : (
            <ListItem>
              <ListItemText
                primary="Нет позиций в заказе"
                sx={{ textAlign: 'center', color: 'text.secondary' }}
              />
            </ListItem>
          )}
        </List>

        {selectedItems.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Выбрано позиций: <strong>{selectedItems.length}</strong>
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Эта информация будет отправлена клиенту для корректировки заказа
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          size="large"
          fullWidth
          disabled={isProcessing}
        >
          Отмена
        </Button>
        
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={<Send />}
          disabled={isProcessing}
        >
          {isProcessing ? 'Отправка...' : 'Отправить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnavailableItemsModal;

